import { PaginationController } from '@clockout-dev/design-elements';
import {
    LEDGER_FEE_FILTER_VALUE,
    LEDGER_FILTERABLE_COLUMNS,
    LEDGER_SORT_BY_COLUMN_KEY,
    LEDGER_SORT_DEFAULT,
    LEDGER_SORT_DIR,
    LEDGER_TYPE_FILTER_OPTIONS,
} from '../constants/ledger.constants';

/**
 * Pure rules for reading and writing the ledger table's state — the query
 * string it persists, the params the endpoint takes, the fee a row carries and
 * whether it is an admin reconciliation. Named exports, because none of this
 * touches component state: a page controller that only needs to read the URL
 * back should not have to inherit fifteen JSX cell renderers to get at it, and
 * the CSV export applies the same rules without instantiating a component.
 *
 * Date and zone formatting lives beside the other date helpers in
 * `utils/dates.js`.
 */

// The design system owns the query-string contract; one instance is enough,
// since it holds no state of its own either.
const tableUrlState = new PaginationController();

/**
 * antd's `sortOrder` for a column, from the sort the server actually applied.
 * Delegated to the design system rather than re-implemented.
 *
 * @param {string} columnKey
 * @param {{sortBy?: string, sortDir?: string} | null} [sort]
 * @returns {'ascend' | 'descend' | null}
 */
export function getLedgerSortOrder(columnKey, sort) {
    return tableUrlState.getTableSortOrder(columnKey, sort);
}

/**
 * Type-filter options, minus service fees unless `showFees` says otherwise.
 *
 * @param {boolean} [showFees] offer service-fee rows as a filter choice
 * @returns {Array<{text: string, value: string}>}
 */
export function getLedgerTypeFilterOptions(showFees = false) {
    return LEDGER_TYPE_FILTER_OPTIONS.filter((option) => showFees || option.value !== LEDGER_FEE_FILTER_VALUE);
}

/**
 * Table state as it currently stands in the URL, in the vocabulary the table
 * itself uses: `sort.sortBy` is a COLUMN KEY, not the API's sort field. The two
 * differ (the Processed column keys on `processed_at` but the endpoint orders by
 * `settled_on`), and keeping the UI's name in the URL is what lets
 * `getTableSortOrder` mark the header without a reverse lookup.
 *
 * `sort` is null when nothing is sorted, which is NOT the same as "sorted by
 * created_at". The endpoint falls back to created_at DESC on its own, so a null
 * sort still returns rows newest-first — but no header is marked, and the
 * Created column starts as clickable as any other. Modelling the server's
 * fallback as an active sort made that column look permanently sorted and made
 * clicking it a no-op: antd would cycle to "unsorted", which read back as the
 * default again, so nothing ever changed.
 *
 * @param {object} [options]
 * @param {number} [options.defaultPageSize=10]
 * @param {boolean} [options.showFees=false] must match the flag handed to
 *   `getLedgerColumns`, or a type the dropdown does not offer stays active
 * @returns {{page: number, perPage: number, sort: {sortBy: string, sortDir: string}|null, type: string|null}}
 */
export function getLedgerStateFromUrl({ defaultPageSize = 10, showFees = false } = {}) {
    const { page, perPage, sortBy, sortDir } = tableUrlState.getTableStateFromUrl({ defaultPageSize });
    const filters = tableUrlState.getTableFiltersFromUrl(LEDGER_FILTERABLE_COLUMNS);
    // Drop a type the table does not offer: a stale link with
    // `?type=service_fee` would otherwise filter by a value that has no
    // dropdown entry to clear it. Keep this `showFees` in step with the one
    // handed to getLedgerColumns.
    const offered = new Set(getLedgerTypeFilterOptions(showFees).map((option) => option.value));
    const selected = (filters?.type || []).filter((value) => offered.has(value));

    return {
        page,
        perPage,
        // An unsortable or unknown column in the URL reads as unsorted rather
        // than reaching the API, which answers 400 for anything off its sort
        // allowlist.
        sort:
            sortBy && LEDGER_SORT_BY_COLUMN_KEY[sortBy]
                ? { sortBy, sortDir: sortDir === LEDGER_SORT_DIR.ASC ? LEDGER_SORT_DIR.ASC : LEDGER_SORT_DIR.DESC }
                : null,
        type: selected.length ? selected.join(',') : null,
    };
}

/**
 * Query params for `fetchLedger`, omitting the ones that match the server's
 * defaults so the request stays as small as what it replaces.
 *
 * @param {{clientId?: string, page?: number, perPage?: number, sort?: object, type?: string|null}} params
 * @returns {object}
 */
export function buildLedgerParams({ clientId, page, perPage, sort, type } = {}) {
    const params = {};
    if (clientId) params.clientId = clientId;
    if (page) params.page = page;
    if (perPage) params.pageSize = perPage;

    // The single translation point between the table's column keys and the
    // endpoint's sort allowlist (`processed_at` -> `settled_on`).
    const sortBy = LEDGER_SORT_BY_COLUMN_KEY[sort?.sortBy];
    if (sortBy && sortBy !== LEDGER_SORT_DEFAULT.sortBy) params.sortBy = sortBy;
    if (sort?.sortDir && sort.sortDir !== LEDGER_SORT_DEFAULT.sortDir) params.sortDir = sort.sortDir;
    if (type) params.type = type;
    return params;
}

/**
 * The fee a row carries, as a number, or null when it is genuinely unknown.
 *
 * The server has already decided this and says so in `fee_source`: `fee: 0`
 * with `standard_no_fee` for an advance that is free by construction (a
 * service_fee row is only ever written for an INSTANT transfer), a real figure
 * with `service_fee_row` or `settlement_split`, and `fee: null` with
 * `fee_source: null` for everything it could not resolve — an instant advance
 * whose fee row was never linked, a settlement whose `fee_amount` is NULL
 * (multi-settle periods, admin reconcile's cross-period rows), or a service_fee
 * row, whose own amount IS the fee.
 *
 * So this reads the answer rather than re-deriving it from `rawType` /
 * `transfer_type`. That derivation lived in the client as well as in SQL and
 * the two disagreed on a service_fee row — the client called it free, the
 * service called it unknown — which is the same duplication the Processed
 * column already dropped. `buildLedgerItem` in clockout-service states the
 * contract: a null fee "must render as '--', never as $0.00", because `$0.00`
 * would be a false statement about money the member did pay.
 *
 * @param {number|string|null} fee
 * @returns {number|null} null only when the server reports the fee unknown
 */
export function resolveFee(fee) {
    return fee != null ? Number(fee) : null;
}

/**
 * Whether the endpoint flagged this row as an admin reconciliation
 * (`is_reconciliation`, clockout-service #316). A real boolean only — the
 * service never sends anything else, and a string `'true'` from a hand-built
 * fixture must not read as one.
 *
 * @param {object|null|undefined} record a consumer-ledger item
 * @returns {boolean}
 */
export function isReconciliationRow(record) {
    return record?.is_reconciliation === true;
}
